import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import companyApi from '@/features/dashboard/company/api/companyApi.ts';
import { companyApiQueryKeys } from '@/features/dashboard/company/api/companyApi.queryKeys.ts';
import useUploadCompanyLogo from '@/features/dashboard/company/hooks/useUploadCompanyLogo.ts';
import { CompanyNameFormData } from '@/features/dashboard/company/schemas/companyNameSchema.ts';
import { CompanyLogoFormData } from '@/features/dashboard/company/schemas/companyLogoSchema.ts';
import { FileAction } from '@/schemas/fileFieldSchema.ts';

interface UpdateCompanyInfoParams {
  /**
   * Nazwa i logo mają osobne modale, ale backend przyjmuje je jedną zmianą —
   * modal dokłada do swojego pola bieżącą wartość drugiego.
   */
  data: CompanyNameFormData & CompanyLogoFormData;
}

export default function useUpdateCompanyInfo() {
  const queryClient = useQueryClient();
  const uploadMutation = useUploadCompanyLogo();

  const mutation = useMutation({
    mutationFn: async ({ data }: UpdateCompanyInfoParams) => {
      let companyLogoFileId: string | null | undefined;

      if (
        data.logo.action === FileAction.CREATE ||
        data.logo.action === FileAction.UPDATE
      ) {
        const result = await uploadMutation.mutateAsync(data.logo.file!);
        companyLogoFileId = result.fileId;
      } else if (data.logo.action === FileAction.DELETE) {
        await companyApi.removeCompanyLogo();
        companyLogoFileId = null;
      } else {
        companyLogoFileId = data.logo.fileId ?? null;
      }

      return companyApi.updateCompanyInfo({
        companyName: data.companyName,
        companyLogoFileId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyApiQueryKeys.companyInfo(),
      });
      toast.success('Dane firmy zostały zaktualizowane');
    },
    onError: (error: Error) => {
      toast.error('Wystąpił błąd', {
        description:
          error.message || 'Wystąpił błąd podczas aktualizacji danych firmy.',
      });
    },
  });

  return {
    ...mutation,
    isPending: mutation.isPending || uploadMutation.isPending,
  };
}
